import React, { useEffect, useState, useMemo, useCallback } from "react";
import AsyncSelect from "react-select/async";
import { apiCallGeneric } from "../../services/api-service";
import { sanitizeInput } from "./security-utils";

/**
 * CommonAsyncMultiSelect Component
 * Standardized asynchronous multi-select dropdown with VAPT security hardening and performance optimization.
 */

const CommonAsyncMultiSelect = ({
  apiUrl,
  placeholder = "Search...",
  onChange,
  value = [],
  isMulti = true,
  isClearable = false,
  styles = null,
  additionalParams = null, // Function that returns additional params based on current value
}) => {
  const [defaultOptions, setDefaultOptions] = useState([]);

  // Memoized initial params preparation logic
  const getParams = useCallback(
    (inputValue) => {
      const params = { term: inputValue || "" };
      if (additionalParams && typeof additionalParams === "function") {
        try {
          const extraParams = additionalParams(value);
          Object.assign(params, extraParams);
        } catch (err) {
          // Silent catch for param calculation errors to prevent app crash
        }
      }
      return params;
    },
    [additionalParams, value]
  );

  // Fetch initial options on mount or when apiUrl changes
  useEffect(() => {
    const loadDefaultOptions = async () => {
      try {
        if (!apiUrl) return;

        const response = await apiCallGeneric("GET", apiUrl, null, {
          params: getParams(""),
        });

        if (response && response.status === 200 && response.data.success) {
          const list = response.data.suggestionlist || [];
          const options = list.map((item) => ({
            value: String(item.value),
            label: sanitizeInput(String(item.label || "")),
          }));
          setDefaultOptions(options);
        }
      } catch (err) {
        // Error handled by api-service/interceptor internally for VAPT
      }
    };

    loadDefaultOptions();
  }, [apiUrl, getParams]);

  // Handle asynchronous loading of options based on search input
  const loadOptions = async (inputValue, callback) => {
    try {
      // If no input value and we have defaults, return them
      if (!inputValue || inputValue.trim() === "") {
        callback(defaultOptions);
        return;
      }

      if (!apiUrl) {
        callback([]);
        return;
      }

      const response = await apiCallGeneric("GET", apiUrl, null, {
        params: getParams(inputValue),
      });

      if (response && response.status === 200 && response.data.success) {
        const list = response.data.suggestionlist || [];
        const options = list.map((item) => ({
          value: String(item.value),
          label: sanitizeInput(String(item.label || "")),
        }));
        callback(options);
      } else {
        callback([]);
      }
    } catch (err) {
      callback([]);
    }
  };

  // Custom styles for the select component, memoized to prevent unnecessary re-calculatios
  const multiselectCustomStyles = useMemo(
    () => ({
      control: (provided) => ({
        ...provided,
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        border: "0.7px solid #E5E5E5",
        padding: "2px",
        fontWeight: "500",
        fontSize: "13px",
        color: "#212129",
        boxShadow: "none",
        "&:hover": {
          borderColor: "#3469E7",
        },
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? "#3469E7" : state.isFocused ? "#F0F4FF" : "#fff",
        color: state.isSelected ? "#fff" : "#212129",
        padding: "10px",
        cursor: "pointer",
        fontSize: "13px",
      }),
      multiValue: (provided) => ({
        ...provided,
        backgroundColor: "#F0F4FF",
        color: "#3469E7",
        borderRadius: "6px",
        padding: "1px 4px",
      }),
      multiValueLabel: (provided) => ({
        ...provided,
        color: "#3469E7",
        fontSize: "12px",
        fontWeight: "500",
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        color: "#3469E7",
        cursor: "pointer",
        ":hover": {
          backgroundColor: "#3469E7",
          color: "white",
        },
      }),
      placeholder: (provided) => ({
        ...provided,
        color: "#A2A7AE",
        fontSize: "13px",
      }),
    }),
    []
  );

  return (
    <AsyncSelect
      isMulti={isMulti}
      cacheOptions
      value={value}
      loadOptions={loadOptions}
      onChange={onChange}
      defaultOptions={defaultOptions}
      placeholder={placeholder}
      styles={styles || multiselectCustomStyles}
      isClearable={isClearable}
      classNamePrefix="async-select"
    />
  );
};

export default CommonAsyncMultiSelect;
